import { useCallback, useEffect, useMemo, useState } from 'react';
import OfferingNotice from '../../components/OfferingNotice';
import PageHeader from '../../components/layout/PageHeader';
import StatusBadge, { type BadgeTone } from '../../components/StatusBadge';
import MyCommitmentsCard from '../../components/commitments/MyCommitmentsCard';
import ProductDetailCard from '../../components/client/ProductDetailCard';
import HoldingValuation from '../../components/client/HoldingValuation';
import NavCorrectionsCard from '../../components/client/NavCorrectionsCard';
import portfolioService, { type ProductSummary } from '../../services/portfolio.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { productOptions, type ProductOption } from '../../lib/productOptions';
import { formatDateTime, formatMoney } from '../../lib/datetime';
import { colors } from '../../styles/theme';
import YieldSimulatorWidget from '../../components/YieldSimulatorWidget';
import type {
  ClientDocument,
  ClientDocumentKind,
  LedgerCategory,
  LedgerMovement,
  ProductBalance,
  Statement,
} from '../../types/finance';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const CATEGORY_TONE: Record<LedgerCategory, BadgeTone> = {
  deposit:       'success',
  yield_accrual: 'info',
  withdrawal:    'warning',
  fee:           'neutral',
  reversal:      'danger',
  adjustment:    'neutral',
  nav_revaluation: 'info',
};

/** The word a client reads for a movement's category (a share of the result is stored as `yield_accrual`). */
function categoryLabel(category: LedgerCategory): string {
  if (category === 'yield_accrual') return 'result';
  if (category === 'fee') return 'management fee';
  if (category === 'nav_revaluation') return 'change in value';
  return category.replace(/_/g, ' ');
}

interface Filters {
  productId: string;
  year: string;
  month: string;
}

const EMPTY_FILTERS: Filters = { productId: '', year: '', month: '' };

export default function ClientPortfolio(): JSX.Element {
  const [products, setProducts] = useState<ProductSummary[]>([]);
  const [balances, setBalances] = useState<ProductBalance[]>([]);
  const [movements, setMovements] = useState<LedgerMovement[]>([]);
  const [movementTotal, setMovementTotal] = useState(0);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [loadingMovements, setLoadingMovements] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Boot: balances + product catalogue. These don't depend on filters.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [bal, prods] = await Promise.all([
          portfolioService.getMyBalance(),
          portfolioService.listProducts(),
        ]);
        if (!cancelled) {
          setBalances(bal.items);
          setProducts(prods);
        }
      } catch (e: unknown) {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load your holdings.'));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const reloadMovements = useCallback(async () => {
    setLoadingMovements(true);
    setError(null);
    try {
      const params: Parameters<typeof portfolioService.getMyMovements>[0] = { size: 100 };
      if (filters.productId) params.product_id = filters.productId;
      if (filters.year) params.year = Number(filters.year);
      if (filters.month) params.month = Number(filters.month);
      const res = await portfolioService.getMyMovements(params);
      setMovements(res.items);
      setMovementTotal(res.total);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load movements.'));
    } finally {
      setLoadingMovements(false);
    }
  }, [filters]);

  useEffect(() => { reloadMovements(); }, [reloadMovements]);

  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    return [now, now - 1, now - 2, now - 3, now - 4];
  }, []);

  const monthDisabled = !filters.year;
  // What is offered to them now, plus every product they hold: a fund withdrawn from new
  // business is still theirs to look up, and to ask statements of.
  const pickable = useMemo(
    () =>
      productOptions(
        products.map((p) => ({ id: p.id, name: p.name })),
        balances.map((b) => ({ id: b.product_id, name: b.product_name })),
      ),
    [products, balances],
  );

  return (
    <>
      <PageHeader
        title="Holdings"
        subtitle="Your balances and recent activity. Statements (PDF, year/month) ship in the next release."
      />

      {error && (
        <div
          className="mb-4 p-3 rounded-lg text-sm"
          role="alert"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {/* Corrections of a price per unit (S-52) — hidden when there are none. */}
      <NavCorrectionsCard />

      {/* Balances */}
      <BalanceSection balances={balances} />

      {/* Products catalogue — renders once products load */}
      {products.length > 0 && <ProductsCatalogue products={products} />}

      {/* Yield simulator — shown once products load; hidden if none are fixed-income */}
      <YieldSimulatorWidget products={products} />

      {/* Deposit commitments — hidden when client has none. */}
      <div className="mb-4">
        <MyCommitmentsCard />
      </div>

      {/* Filters */}
      <div
        className="rounded-2xl p-4 mb-4 flex flex-wrap gap-3 items-end"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <Field label="Series" htmlFor="movements-series">
          <select
            id="movements-series"
            value={filters.productId}
            onChange={(e) => setFilters((f) => ({ ...f, productId: e.target.value }))}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">All series</option>
            {pickable.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>

        <Field label="Year" htmlFor="movements-year">
          <select
            id="movements-year"
            value={filters.year}
            onChange={(e) => {
              const newYear = e.target.value;
              setFilters((f) => ({
                ...f,
                year: newYear,
                // Clearing the year MUST clear the month — backend rejects month-without-year.
                month: newYear ? f.month : '',
              }));
            }}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">Any year</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </Field>

        <Field label="Month" htmlFor="movements-month">
          <select
            id="movements-month"
            value={filters.month}
            onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))}
            disabled={monthDisabled}
            className="px-3 py-2 rounded-lg text-sm"
            style={{
              border: `1px solid ${colors.controlBorder}`,
              background: colors.surface,
              cursor: monthDisabled ? 'not-allowed' : 'auto',
              opacity: monthDisabled ? 0.5 : 1,
            }}
          >
            <option value="">Any month</option>
            {MONTH_LABELS.map((label, i) => (
              <option key={label} value={String(i + 1)}>{label}</option>
            ))}
          </select>
        </Field>

        {(filters.productId || filters.year || filters.month) && (
          <button
            type="button"
            onClick={() => setFilters(EMPTY_FILTERS)}
            className="px-3 py-2 rounded-lg text-xs font-semibold"
            style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Movements */}
      <MovementsSection
        movements={movements}
        total={movementTotal}
        loading={loadingMovements}
      />

      {/* Statements */}
      <StatementsSection products={pickable} />

      {/* Client documents (0118): one section per kind */}
      <DocumentsSection
        kind="contribution_receipt"
        title="Contribution receipts"
        emptyText="A receipt appears here for each contribution once it is approved."
        footnote="A receipt records a contribution we received. It is not a tax document."
        dateLabel="Approved"
      />
      <DocumentsSection
        kind="payout_receipt"
        title="Withdrawal receipts"
        emptyText="A receipt appears here for each withdrawal once the transfer has settled."
        footnote="A receipt records a transfer we sent. It is not a tax document."
        dateLabel="Settled"
      />
      <DocumentsSection
        kind="bank_return_notice"
        title="Transfer return notices"
        emptyText="If a transfer could not be applied and was returned, the notice appears here."
        footnote="A notice records a transfer returned to the account it came from."
        dateLabel="Returned"
      />
      <OfferingNotice />
    </>
  );
}

function ProductsCatalogue({ products }: Readonly<{ products: ProductSummary[] }>): JSX.Element {
  return (
    <section className="mb-5">
      <h2
        className="text-sm font-semibold mb-3 uppercase tracking-wider"
        style={{ color: colors.muted }}
      >
        Available series
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {products.map((p) => (
          <ProductDetailCard key={p.id} product={p} />
        ))}
      </div>
    </section>
  );
}

function BalanceSection({ balances }: Readonly<{ balances: ProductBalance[] }>): JSX.Element {
  if (balances.length === 0) {
    return (
      <div
        className="rounded-2xl p-6 mb-5 text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        No movements yet. Once your first transfer arrives and is confirmed, your balance will
        appear here. <a href="/client/fund" style={{ color: colors.ink, fontWeight: 600 }}>Make a contribution →</a>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-5">
      {balances.map((b) => (
        <div
          key={b.product_id}
          className="rounded-2xl p-5"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <p className="text-xs font-semibold uppercase tracking-widest mb-1" style={{ color: colors.muted }}>
            {b.product_name}
          </p>
          <p className="text-2xl font-bold font-mono" style={{ color: colors.ink }}>
            {formatMoney(b.balance, b.currency_code)}
          </p>
          {b.nav && <HoldingValuation nav={b.nav} currency={b.currency_code} />}
          <p className="text-xs mt-2" style={{ color: colors.mutedSoft }}>
            Last activity{' '}
            {b.last_movement_at ? formatDateTime(b.last_movement_at) : '—'}
          </p>
        </div>
      ))}
    </div>
  );
}

interface MovementsProps {
  movements: LedgerMovement[];
  total: number;
  loading: boolean;
}

function MovementsSection({ movements, total, loading }: Readonly<MovementsProps>): JSX.Element {
  if (loading) {
    return <p className="text-sm" style={{ color: colors.muted }}>Loading movements…</p>;
  }
  if (movements.length === 0) {
    return (
      <div
        className="rounded-2xl p-8 text-center text-sm"
        style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
      >
        No movements match the current filters.
      </div>
    );
  }
  return (
    <>
      <div className="text-xs mb-2" style={{ color: colors.mutedSoft }}>
        Showing {movements.length} of {total} movement{total === 1 ? '' : 's'}.
      </div>
      <div
        className="overflow-hidden rounded-2xl"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <table className="w-full text-sm">
          <thead style={{ background: colors.canvas, color: colors.muted }}>
            <tr>
              <th className="text-left font-medium px-4 py-3">Date</th>
              <th className="text-left font-medium px-4 py-3">Series</th>
              <th className="text-left font-medium px-4 py-3">Category</th>
              <th className="text-right font-medium px-4 py-3">Amount</th>
              <th className="text-left font-medium px-4 py-3">Reference</th>
            </tr>
          </thead>
          <tbody>
            {movements.map((m, idx) => {
              const sign = m.entry_type === 'credit' ? '+' : '−';
              const isPositive = m.entry_type === 'credit';
              return (
                <tr
                  key={m.id}
                  style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
                >
                  <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                    {formatDateTime(m.created_at)}
                  </td>
                  <td className="px-4 py-3" style={{ color: colors.inkSoft }}>
                    {m.product_name ?? '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge tone={CATEGORY_TONE[m.category]}>
                      {categoryLabel(m.category)}
                    </StatusBadge>
                    {m.description && (
                      <div className="text-xs mt-1 truncate max-w-[180px]" style={{ color: colors.muted }}>
                        {m.description}
                      </div>
                    )}
                  </td>
                  <td
                    className="px-4 py-3 text-right font-mono font-semibold"
                    style={{ color: isPositive ? colors.success : colors.ink }}
                  >
                    {sign}{formatMoney(m.amount, m.currency_code)}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.muted }}>
                    {m.reference_number ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: Readonly<{ label: string; htmlFor: string; children: React.ReactNode }>): JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} className="block text-xs font-semibold mb-1" style={{ color: colors.inkSoft }}>
        {label}
      </label>
      {children}
    </div>
  );
}


// ─── Statements section ──────────────────────────────────────────
interface StatementsSectionProps {
  products: ProductOption[];
}

function StatementsSection({ products }: Readonly<StatementsSectionProps>): JSX.Element {
  const [statements, setStatements] = useState<Statement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterYear, setFilterYear] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [filterProductId, setFilterProductId] = useState<string>('');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Parameters<typeof portfolioService.listMyStatements>[0] = {};
      if (filterYear) params.year = Number(filterYear);
      if (filterMonth) params.month = Number(filterMonth);
      if (filterProductId) params.product_id = filterProductId;
      const items = await portfolioService.listMyStatements(params);
      setStatements(items);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not load statements.'));
    } finally {
      setLoading(false);
    }
  }, [filterYear, filterMonth, filterProductId]);

  useEffect(() => { reload(); }, [reload]);

  const yearOptions = useMemo(() => {
    const now = new Date().getUTCFullYear();
    return [now, now - 1, now - 2, now - 3, now - 4];
  }, []);

  const handleDownload = async (id: string): Promise<void> => {
    setDownloadingId(id);
    setError(null);
    try {
      const url = await portfolioService.getStatementDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not generate the download link.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className="mt-8">
      <h2
        className="text-sm font-bold uppercase tracking-widest mb-3"
        style={{ color: colors.inkSoft }}
      >
        Monthly statements
      </h2>

      {error && (
        <div
          className="mb-3 p-3 rounded-lg text-sm"
          role="alert"
          style={{ background: colors.dangerBg, border: `1px solid ${colors.dangerBorder}`, color: colors.danger }}
        >
          {error}
        </div>
      )}

      {/* Filters */}
      <div
        className="rounded-2xl p-4 mb-4 flex flex-wrap gap-3 items-end"
        style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <Field label="Year" htmlFor="statements-year">
          <select
            id="statements-year"
            value={filterYear}
            onChange={(e) => setFilterYear(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">Any year</option>
            {yearOptions.map((y) => (
              <option key={y} value={String(y)}>{y}</option>
            ))}
          </select>
        </Field>
        <Field label="Month" htmlFor="statements-month">
          <select
            id="statements-month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">Any month</option>
            {MONTH_LABELS.map((label, i) => (
              <option key={label} value={String(i + 1)}>{label}</option>
            ))}
          </select>
        </Field>
        <Field label="Series" htmlFor="statements-series">
          <select
            id="statements-series"
            value={filterProductId}
            onChange={(e) => setFilterProductId(e.target.value)}
            className="px-3 py-2 rounded-lg text-sm"
            style={{ border: `1px solid ${colors.controlBorder}`, background: colors.surface }}
          >
            <option value="">Any series</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </Field>
        {(filterYear || filterMonth || filterProductId) && (
          <button
            type="button"
            onClick={() => { setFilterYear(''); setFilterMonth(''); setFilterProductId(''); }}
            className="px-3 py-2 rounded-lg text-xs font-semibold"
            style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* List */}
      {loading && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading statements…</p>
      )}
      {!loading && statements.length === 0 && (
        <div
          className="rounded-2xl p-6 text-sm"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
        >
          No statements match the current filters. Statements are generated automatically
          on the 1st of each month for periods with activity, and can be requested below
          for past months.
        </div>
      )}
      {!loading && statements.length > 0 && (
        <div
          className="overflow-hidden rounded-2xl"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <table className="w-full text-sm">
            <thead style={{ background: colors.canvas, color: colors.muted }}>
              <tr>
                <th className="text-left font-medium px-4 py-3">Period</th>
                <th className="text-left font-medium px-4 py-3">Series</th>
                <th className="text-right font-medium px-4 py-3">Closing balance</th>
                <th className="text-right font-medium px-4 py-3">Movements</th>
                <th className="text-left font-medium px-4 py-3">Generated</th>
                <th className="text-right font-medium px-4 py-3 sr-only">Action</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s, idx) => (
                <tr
                  key={s.id}
                  style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
                >
                  <td className="px-4 py-3" style={{ color: colors.ink }}>
                    {MONTH_LABELS[s.period_month - 1]} {s.period_year}
                  </td>
                  <td className="px-4 py-3" style={{ color: colors.inkSoft }}>{s.product_name}</td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                    {formatMoney(s.closing_balance, s.currency_code)}
                  </td>
                  <td className="px-4 py-3 text-right" style={{ color: colors.muted }}>
                    {s.movement_count}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                    {formatDateTime(s.generated_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label={`Download PDF, ${MONTH_LABELS[s.period_month - 1]} ${s.period_year}, ${s.product_name}`}
                      aria-busy={downloadingId === s.id}
                      onClick={() => handleDownload(s.id)}
                      disabled={downloadingId === s.id}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold"
                      style={{
                        background: colors.surface,
                        color: colors.inkSoft,
                        border: `1px solid ${colors.border}`,
                        cursor: downloadingId === s.id ? 'wait' : 'pointer',
                      }}
                    >
                      {downloadingId === s.id ? '…' : 'Download PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs mt-4" style={{ color: colors.muted }}>
        A statement is prepared for each closed month and emailed to you. If one is
        missing, ask your account executive.
      </p>
    </section>
  );
}


// ─── Client documents section (one component, one kind per mount) ─
interface DocumentsSectionProps {
  kind: ClientDocumentKind;
  title: string;
  emptyText: string;
  footnote: string;
  dateLabel: string;
}

function DocumentsSection({
  kind, title, emptyText, footnote, dateLabel,
}: Readonly<DocumentsSectionProps>): JSX.Element {
  const [documents, setDocuments] = useState<ClientDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await portfolioService.listMyDocuments(kind);
        if (!cancelled) setDocuments(items);
      } catch (e: unknown) {
        if (!cancelled) setError(getApiErrorMessage(e, 'Could not load documents.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind]);

  const handleDownload = async (id: string): Promise<void> => {
    setDownloadingId(id);
    setError(null);
    try {
      const url = await portfolioService.getDocumentDownloadUrl(id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, 'Could not generate the download link.'));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <section className="mt-8">
      <h2
        className="text-sm font-bold uppercase tracking-widest mb-3"
        style={{ color: colors.inkSoft }}
      >
        {title}
      </h2>
      {error && (
        <p className="text-sm mb-3" role="alert" style={{ color: colors.danger }}>{error}</p>
      )}
      {loading && (
        <p className="text-sm" style={{ color: colors.muted }}>Loading…</p>
      )}
      {!loading && documents.length === 0 && (
        <div
          className="rounded-2xl p-6 text-sm"
          style={{ background: colors.surface, border: `1px solid ${colors.border}`, color: colors.muted }}
        >
          {emptyText}
        </div>
      )}
      {!loading && documents.length > 0 && (
        <div
          className="overflow-hidden rounded-2xl"
          style={{ background: colors.surface, border: `1px solid ${colors.border}` }}
        >
          <table className="w-full text-sm">
            <thead style={{ background: colors.canvas, color: colors.muted }}>
              <tr>
                <th className="text-left font-medium px-4 py-3">Document</th>
                <th className="text-left font-medium px-4 py-3">Series</th>
                <th className="text-right font-medium px-4 py-3">Amount</th>
                <th className="text-left font-medium px-4 py-3">{dateLabel}</th>
                <th className="text-right font-medium px-4 py-3 sr-only">Action</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((d, idx) => (
                <tr
                  key={d.id}
                  style={{ borderTop: idx === 0 ? 'none' : `1px solid ${colors.borderSoft}` }}
                >
                  <td className="px-4 py-3 font-mono text-xs" style={{ color: colors.ink }}>
                    {d.document_number}
                  </td>
                  <td className="px-4 py-3" style={{ color: colors.inkSoft }}>{d.product_name ?? '—'}</td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: colors.ink }}>
                    {formatMoney(d.amount, d.currency_code)}
                  </td>
                  <td className="px-4 py-3 text-xs" style={{ color: colors.muted }}>
                    {formatDateTime(d.occurred_at)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      aria-label={`Download PDF, ${d.document_number}`}
                      aria-busy={downloadingId === d.id}
                      onClick={() => handleDownload(d.id)}
                      disabled={downloadingId === d.id}
                      className="px-3 py-1.5 rounded-md text-xs font-semibold"
                      style={{
                        background: colors.surface,
                        color: colors.inkSoft,
                        border: `1px solid ${colors.border}`,
                        cursor: downloadingId === d.id ? 'wait' : 'pointer',
                      }}
                    >
                      {downloadingId === d.id ? '…' : 'Download PDF'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-xs mt-4" style={{ color: colors.muted }}>{footnote}</p>
    </section>
  );
}
